import { InlineRangeCalendar } from "PFComponents/calendar/inline_calendar";
import { utc } from "PFCore/helpers/date";
import { FormatFunction } from "PFCore/helpers/date_formatter";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { RefObject } from "react";

import { ComponentProps } from "../component_prop_types";

type InlineRangeCalendarValue = {
  start_date?: string | null;
  end_date?: string | null;
};

const formatRangeBoundary = (date: string | null | undefined, formatISODate: FormatFunction) =>
  (date && formatISODate(utc(date))) || "";

const getFirstErrorMessage = (error?: Record<string, string> | null) => error && Object.values(error)[0];

const resolveRangeChange = (
  start: string | null,
  end: string | null,
  formatDatabaseDateTime: FormatFunction
): InlineRangeCalendarValue | null =>
  // API does not allow null values for start or end date, so a clear must be represented as null
  start === null && end === null
    ? null
    : { start_date: formatDatabaseDateTime(start), end_date: formatDatabaseDateTime(end) };

type InlineRangeCalendarWrapperProps = Pick<
  ComponentProps,
  | "label"
  | "title"
  | "formattedTitle"
  | "qaId"
  | "style"
  | "required"
  | "disabled"
  | "className"
  | "variant"
  | "tabIndex"
  | "inputRef"
> & {
  value?: InlineRangeCalendarValue | null;
  error?: Record<string, string> | null;
  onChange: (value: InlineRangeCalendarValue | null) => void;
  portalRef?: RefObject<HTMLDivElement>;
};

export const InlineRangeCalendarWrapper = ({
  value,
  label,
  onChange,
  qaId,
  style,
  error,
  required,
  disabled,
  className,
  variant,
  portalRef,
  tabIndex,
  inputRef
}: InlineRangeCalendarWrapperProps) => {
  const { formatISODate, formatDatabaseDateTime } = useDateFormatter();
  return (
    <InlineRangeCalendar
      startDate={formatRangeBoundary(value?.start_date, formatISODate)}
      endDate={formatRangeBoundary(value?.end_date, formatISODate)}
      label={label}
      qaId={qaId}
      required={required}
      error={getFirstErrorMessage(error)}
      handleChange={(start, end) => onChange(resolveRangeChange(start, end, formatDatabaseDateTime))}
      letClear
      disabled={disabled}
      portalRef={portalRef}
      variant={variant}
      inputFieldSetProps={{
        inputClassName: className
      }}
      tabIndex={tabIndex}
      style={style}
      inputRef={inputRef}
    />
  );
};
